import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../lib/api';
import SiteView from '../components/dashboard/SiteView';

interface Site {
  id: string;
  name: string;
  url: string;
  status: 'pending' | 'connected' | 'disconnected' | 'error';
  content_types: string[];
  last_sync_at: string | null;
  stats: {
    total_posts: number;
    total_products: number;
    total_users: number;
  };
  created_at: string;
  wp_username: string | null;
}

export default function SitePage() {
  const { siteId, plugin = 'wordpress', contentType = 'posts' } = useParams<{
    siteId: string;
    plugin?: string;
    contentType?: string;
  }>();
  const navigate = useNavigate();
  const [site, setSite] = useState<Site | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!siteId) return;

    async function fetchSite() {
      try {
        setLoading(true);
        setError(null);
        const data = await api.get<{ site: Site }>(`/api/v1/sites/${siteId}`);
        setSite(data.site);
      } catch (err) {
        console.error('Failed to fetch site:', err);
        setError(err instanceof Error ? err.message : 'Failed to load site');
      } finally {
        setLoading(false);
      }
    }

    fetchSite();
  }, [siteId]);

  const handleBack = () => {
    navigate('/');
  };

  const handlePluginChange = (newPlugin: string) => {
    // Navigate to new plugin with default content type
    const defaultContent = newPlugin === 'wordpress' ? 'posts' :
                           newPlugin === 'woocommerce' ? 'products' : 'posts';
    navigate(`/sites/${siteId}/${newPlugin}/${defaultContent}`);
  };

  const handleContentTypeChange = (newContentType: string) => {
    navigate(`/sites/${siteId}/${plugin}/${newContentType}`);
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900">Site not found</h3>
          <p className="text-sm text-gray-500 mt-1">{error || 'The site you\'re looking for doesn\'t exist.'}</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <SiteView
      siteId={site.id}
      siteName={site.name}
      siteUrl={site.url}
      wpUsername={site.wp_username}
      enabledContentTypes={site.content_types || ['posts', 'pages', 'media']}
      activePlugin={plugin || 'wordpress'}
      activeContentType={contentType || 'posts'}
      onBack={handleBack}
      onPluginChange={handlePluginChange}
      onContentTypeChange={handleContentTypeChange}
    />
  );
}
